import BigNumber from "bignumber.js";
import web3 from "../App/Service/web3";

const WEI_UNIT = new BigNumber(10).pow(18);

/**
 * Safely converts an on-chain Wei value (string, number, BN, or BigNumber) to Ether string.
 * Uses BigNumber for high-precision arithmetic to prevent IEEE 754 floating point rounding errors.
 *
 * @param {string|number|BigNumber} val - Value in Wei
 * @param {number} [decimals] - Optional fixed decimal places to format to
 * @returns {string} Ether value as formatted string
 */
export const safeFromWei = (val, decimals) => {
  if (val === null || val === undefined || val === "" || val === "0" || val === 0) {
    return decimals !== undefined ? (0).toFixed(decimals) : "0";
  }

  try {
    const rawStr = typeof val === "string" ? val.trim() : String(val);
    const bn = new BigNumber(rawStr);

    if (bn.isNaN() || !bn.isFinite()) {
      return decimals !== undefined ? (0).toFixed(decimals) : "0";
    }

    const inEther = bn.dividedBy(WEI_UNIT);
    return decimals !== undefined ? inEther.toFixed(decimals) : inEther.toString();
  } catch (err) {
    // Fallback to web3.utils.fromWei if available
    try {
      if (web3 && web3.utils && web3.utils.fromWei) {
        const fallback = web3.utils.fromWei(`${val}`, "ether");
        return decimals !== undefined ? Number(fallback).toFixed(decimals) : fallback;
      }
    } catch (e) {
      // ignore
    }
    return decimals !== undefined ? (0).toFixed(decimals) : "0";
  }
};

/**
 * Accurately sums an array of Wei values using BigNumber arithmetic and returns the result in Ether.
 *
 * @param {Array<string|number|BigNumber>} weiList - Array of Wei amounts
 * @param {number} [decimals=2] - Decimal places to format the final Ether sum (e.g. 2 for "123.45")
 * @returns {string} Formatted total prize sum in Ether
 */
export const sumWeiToEther = (weiList, decimals = 3) => {
  if (!Array.isArray(weiList) || weiList.length === 0) {
    return (0).toFixed(decimals);
  }

  try {
    let totalBN = new BigNumber(0);

    for (const item of weiList) {
      if (item === null || item === undefined || item === "" || item === "0" || item === 0) {
        continue;
      }

      const rawStr = typeof item === "string" ? item.trim() : String(item);
      const bn = new BigNumber(rawStr);

      if (!bn.isNaN() && bn.isFinite() && bn.isPositive()) {
        totalBN = totalBN.plus(bn);
      }
    }

    const inEther = totalBN.dividedBy(WEI_UNIT);
    return inEther.toFixed(decimals);
  } catch (err) {
    console.warn("[sumWeiToEther] Error calculating total sum:", err);
    return (0).toFixed(decimals);
  }
};

/**
 * Finds the minimum non-zero Wei amount in an array and returns it in Ether format.
 *
 * @param {Array<string|number|BigNumber>} weiList - Array of Wei amounts
 * @param {number} [decimals=3] - Decimal places to format the lowest amount
 * @returns {string} Lowest amount in Ether format
 */
export const findLowestWeiToEther = (weiList, decimals = 3) => {
  if (!Array.isArray(weiList) || weiList.length === 0) {
    return (0).toFixed(decimals);
  }

  try {
    let minBN = null;

    for (const item of weiList) {
      if (item === null || item === undefined || item === "" || item === "0" || item === 0) {
        continue;
      }

      const rawStr = typeof item === "string" ? item.trim() : String(item);
      const bn = new BigNumber(rawStr);

      if (!bn.isNaN() && bn.isFinite() && bn.isPositive()) {
        if (minBN === null || bn.isLessThan(minBN)) {
          minBN = bn;
        }
      }
    }

    if (minBN === null) {
      return (0).toFixed(decimals);
    }

    const inEther = minBN.dividedBy(WEI_UNIT);
    return inEther.toFixed(decimals);
  } catch (err) {
    console.warn("[findLowestWeiToEther] Error finding lowest amount:", err);
    return (0).toFixed(decimals);
  }
};

/**
 * Adds two Wei amounts safely.
 */
export const addWei = (a, b) => {
  try {
    const bnA = new BigNumber(String(a || 0).trim());
    const bnB = new BigNumber(String(b || 0).trim());
    return bnA.plus(bnB).toString();
  } catch (e) {
    return "0";
  }
};

/**
 * Compares two Wei amounts.
 * Returns 1 if a > b, -1 if a < b, 0 if a === b.
 */
export const compareWei = (a, b) => {
  try {
    const bnA = new BigNumber(String(a || 0).trim());
    const bnB = new BigNumber(String(b || 0).trim());
    if (bnA.isGreaterThan(bnB)) return 1;
    if (bnA.isLessThan(bnB)) return -1;
    return 0;
  } catch (e) {
    return 0;
  }
};

export { BigNumber };
export default safeFromWei;
